import { makeAutoObservable } from 'mobx'
import { type IRowBase, type TableStore } from 'shared/ui/TableNew'

export class TableBulkStore<TRow extends IRowBase = IRowBase> {
  private _selectedRowsMap: Map<TRow['id'], TRow> = new Map()
  private _bulkAll = false
  private _total = 0
  private _lastClickedRowId: TRow['id'] | null = null

  constructor(private _tableStore: TableStore<TRow>) {
    makeAutoObservable(this)
  }

  private get _config() {
    return this._tableStore.config
  }

  get selectedRows() {
    return Array.from(this._selectedRowsMap.values())
  }

  get selectedIds() {
    return Array.from(this._selectedRowsMap.keys())
  }

  get isSelected() {
    return Boolean(this.selectedIds.length)
  }

  get bulkAllMode() {
    return this._bulkAll
  }

  get isSelectedAllOnPages() {
    const selectedRows = this._tableStore.rows.filter(
      (row) => !row.isDisabled && !row.origin?.isDisabled
    )

    return selectedRows.map((row) => row.id).every((id) => this._selectedRowsMap.has(id))
  }

  get bulkTotal() {
    return this._total
  }

  resetBulkAll = () => {
    this._bulkAll = false
  }

  resetSelected = () => {
    this._selectedRowsMap.clear()
    this._bulkAll = false
    this._lastClickedRowId = null
  }

  onRowSelect = (row: TRow, isSelected: boolean, isShiftKey = false) => {
    this._bulkAll = false

    if (isShiftKey && isSelected && this._lastClickedRowId !== null && this.onRowSelectRange(row)) {
      this._lastClickedRowId = row.id
      return
    }

    if (isSelected) {
      this._selectedRowsMap.set(row.id, row)
    } else {
      this._selectedRowsMap.delete(row.id)
      this._config?.resetSelectAllCallback?.()
    }

    this._lastClickedRowId = isSelected ? row.id : this._lastClickedRowId
  }

  private onRowSelectRange = (row: TRow): boolean => {
    const rows = this._tableStore.rows
    const lastClickedIndex = rows.findIndex((r) => r.id === this._lastClickedRowId)
    const currentIndex = rows.findIndex((r) => r.id === row.id)

    if (lastClickedIndex === -1 || currentIndex === -1) return false

    const start = Math.min(lastClickedIndex, currentIndex)
    const end = Math.max(lastClickedIndex, currentIndex)

    for (let i = start; i <= end; i++) {
      const rangeRow = rows[i]

      if (
        !rangeRow.isDisabled &&
        !rangeRow.isCheckboxDisabled &&
        !rangeRow.origin?.isDisabled &&
        !rangeRow.origin?.isCheckboxDisabled
      ) {
        this._selectedRowsMap.set(rangeRow.id, rangeRow)
      }
    }

    return true
  }

  selectAllOnPageIds = () => {
    this._tableStore.rows.forEach((row) => {
      if (!row.isDisabled && !row.origin?.isDisabled) this._selectedRowsMap.set(row.id, row)
    })
    this._lastClickedRowId = null
  }

  unselectAllIds = () => {
    this._selectedRowsMap.clear()
    this._bulkAll = false
    this._lastClickedRowId = null
    this._config?.resetSelectAllCallback?.()
  }

  toggleSelectAll = () => {
    this.isSelectedAllOnPages ? this.unselectAllIds() : this.selectAllOnPageIds()
  }

  changeBulkAllMode = (condition: boolean) => {
    if (condition) {
      this.selectAllOnPageIds()
      this._config?.selectAllCallback?.()
    } else {
      this.unselectAllIds()
      this._config?.resetSelectAllCallback?.()
    }
    this._bulkAll = condition
  }

  setBulkTotal = (total: number) => {
    this._total = total
  }
}
